export type EmarTimingMeta = {
  parsed: boolean;
  scheduledDate: Date | null;
  scheduledLabel: string;
  isDueSoon: boolean;
  isUpcoming: boolean;
  isOverdue: boolean;
};

export type EmarMedicationOccurrence = {
  occurrenceKey: string;
  scheduledTimeKey: string;
  scheduledClockTime: string;
  scheduledLabel: string;
  scheduleText: string;
};

export type MedicationScheduleDayPattern = "daily" | "specific_days" | "alternate_days" | "specific_month_days";

const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;
const WEEKDAY_LABELS: Record<string, string> = {
  sun: "Sun",
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
};
const WEEKDAY_ALIASES: Record<string, string> = {
  sunday: "sun",
  sun: "sun",
  monday: "mon",
  mon: "mon",
  mondays: "mon",
  tuesday: "tue",
  tue: "tue",
  tues: "tue",
  tuesdays: "tue",
  wednesday: "wed",
  wed: "wed",
  wednesdays: "wed",
  thursday: "thu",
  thu: "thu",
  thur: "thu",
  thurs: "thu",
  thursdays: "thu",
  friday: "fri",
  fri: "fri",
  fridays: "fri",
  saturday: "sat",
  sat: "sat",
  saturdays: "sat",
};

const getReferenceDate = (value?: Date | string | null) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }
  const normalized = String(value || "").trim();
  if (normalized) {
    const dateOnlyMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const yearValue = Number(dateOnlyMatch[1]);
      const monthIndex = Number(dateOnlyMatch[2]) - 1;
      const dayValue = Number(dateOnlyMatch[3]);
      const localDate = new Date(yearValue, monthIndex, dayValue, 12, 0, 0, 0);
      if (!Number.isNaN(localDate.getTime())) {
        return localDate;
      }
    }
    const parsed = new Date(normalized);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }
  return new Date();
};

const getWeekdayKeyForDate = (value?: Date | string | null) => (
  WEEKDAY_KEYS[getReferenceDate(value).getDay()] || "sun"
);

const extractScheduleDaysFromText = (value: any) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) {
    return [];
  }
  const matches = Array.from(normalized.matchAll(/\b(sunday|sun|monday|mondays|mon|tuesday|tuesdays|tues|tue|wednesday|wednesdays|wed|thursday|thursdays|thurs|thur|thu|friday|fridays|fri|saturday|saturdays|sat)\b/g));
  return Array.from(
    new Set(
      matches
        .map((match) => WEEKDAY_ALIASES[String(match[1] || "").toLowerCase()])
        .filter(Boolean),
    ),
  );
};

export const normalizeMedicationScheduleMonthDays = (value: any) => Array.from(
  new Set(
    (Array.isArray(value) ? value : [])
      .map((day) => Number(day))
      .filter((day) => Number.isInteger(day) && day >= 1 && day <= 31),
  ),
).sort((first, second) => first - second);

const buildScheduleDayPrefix = (dayPattern: MedicationScheduleDayPattern, scheduleDays: string[], scheduleMonthDays: number[]) => {
  if (dayPattern === "alternate_days") {
    return "Every other day";
  }
  if (dayPattern === "specific_month_days" && scheduleMonthDays.length) {
    return `Monthly on days ${scheduleMonthDays.join(", ")}`;
  }
  if (dayPattern === "specific_days" && scheduleDays.length) {
    return scheduleDays
      .map((day) => WEEKDAY_LABELS[day] || day)
      .join(", ");
  }
  return "";
};

export const normalizeMedicationScheduleDays = (value: any) => Array.from(
  new Set(
    (Array.isArray(value) ? value : [])
      .map((day) => WEEKDAY_ALIASES[String(day || "").trim().toLowerCase()])
      .filter(Boolean),
  ),
).sort((first, second) => WEEKDAY_KEYS.indexOf(first as any) - WEEKDAY_KEYS.indexOf(second as any));

export const getMedicationScheduleDays = (medication: any) => {
  const explicit = normalizeMedicationScheduleDays(medication?.scheduleDays);
  if (explicit.length) {
    return explicit;
  }
  return extractScheduleDaysFromText(medication?.schedule);
};

export const getMedicationScheduleMonthDays = (medication: any) => (
  normalizeMedicationScheduleMonthDays(medication?.scheduleMonthDays)
);

export const inferMedicationScheduleDayPattern = (medication: any): MedicationScheduleDayPattern => {
  const explicit = String(medication?.scheduleDayPattern || "").trim().toLowerCase();
  if (explicit === "specific_days" || explicit === "alternate_days" || explicit === "daily" || explicit === "specific_month_days") {
    return explicit;
  }
  const scheduleText = String(medication?.schedule || "").trim().toLowerCase();
  if (getMedicationScheduleMonthDays(medication).length) {
    return "specific_month_days";
  }
  if (/\b(every other day|alternate days?|alternating days?)\b/.test(scheduleText)) {
    return "alternate_days";
  }
  if (getMedicationScheduleDays(medication).length) {
    return "specific_days";
  }
  return "daily";
};

export const isMedicationScheduledForDate = (medication: any, referenceDate?: Date | string | null) => {
  if (medication?.prn) {
    return true;
  }
  const dayPattern = inferMedicationScheduleDayPattern(medication);
  const scheduleDays = getMedicationScheduleDays(medication);
  const scheduleMonthDays = getMedicationScheduleMonthDays(medication);
  if (dayPattern === "specific_month_days") {
    if (!scheduleMonthDays.length) {
      return false;
    }
    return scheduleMonthDays.includes(getReferenceDate(referenceDate).getDate());
  }
  if (dayPattern === "specific_days") {
    if (!scheduleDays.length) {
      return false;
    }
    return scheduleDays.includes(getWeekdayKeyForDate(referenceDate));
  }
  if (dayPattern === "alternate_days") {
    const targetDate = getReferenceDate(referenceDate);
    const anchorDate = getReferenceDate(
      medication?.effectiveStartDate
      || medication?.createdAt
      || targetDate,
    );
    const target = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate()).getTime();
    const anchor = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), anchorDate.getDate()).getTime();
    return Math.abs(Math.round((target - anchor) / 86400000)) % 2 === 0;
  }
  return true;
};

export const normalizeStructuredScheduleTime = (value: any) => {
  const normalized = String(value || "").trim();
  if (!normalized) {
    return "";
  }
  const match = normalized.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    return "";
  }
  const hoursValue = Number(match[1] || 0);
  const minutesValue = Number(match[2] || 0);
  if (hoursValue < 0 || hoursValue > 23 || minutesValue < 0 || minutesValue > 59) {
    return "";
  }
  return `${String(hoursValue).padStart(2, "0")}:${String(minutesValue).padStart(2, "0")}`;
};

const formatTimeLabel = (date: Date) =>
  date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });

export const formatStructuredScheduleTimeLabel = (value: string) => {
  const normalized = normalizeStructuredScheduleTime(value);
  if (!normalized) {
    return "";
  }
  const [hoursText, minutesText] = normalized.split(":");
  return formatTimeLabel(buildDateForToday(Number(hoursText || 0), Number(minutesText || 0)));
};

const buildDateForToday = (hours: number, minutes: number) => {
  const now = new Date();
  return new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    hours,
    minutes,
    0,
    0,
  );
};

const buildDateForTodayFromStructuredTime = (value: string) => {
  const normalized = normalizeStructuredScheduleTime(value);
  if (!normalized) {
    return null;
  }
  const [hoursText, minutesText] = normalized.split(":");
  return buildDateForToday(Number(hoursText || 0), Number(minutesText || 0));
};

const convertMeridiemMatchToStructuredTime = (match: RegExpMatchArray) => {
  const hoursValue = Number(match[1] || 0);
  const minutesValue = Number(match[2] || 0);
  const meridiem = String(match[3] || "").toLowerCase();
  let normalizedHours = hoursValue % 12;
  if (meridiem === "pm") {
    normalizedHours += 12;
  }
  return normalizeStructuredScheduleTime(`${normalizedHours}:${String(minutesValue).padStart(2, "0")}`);
};

const extractScheduleTimeFromText = (schedule: string) => {
  const normalized = String(schedule || "").trim();
  if (!normalized) {
    return null;
  }

  const meridiemMatch = normalized.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)\b/i);
  if (meridiemMatch) {
    const structuredTime = convertMeridiemMatchToStructuredTime(meridiemMatch);
    const structuredDate = structuredTime ? buildDateForTodayFromStructuredTime(structuredTime) : null;
    if (structuredDate) {
      return structuredDate;
    }
  }

  const twentyFourHourMatch = normalized.match(/\b(\d{1,2}):(\d{2})\b/);
  if (twentyFourHourMatch) {
    const hoursValue = Number(twentyFourHourMatch[1] || 0);
    const minutesValue = Number(twentyFourHourMatch[2] || 0);
    if (hoursValue >= 0 && hoursValue <= 23 && minutesValue >= 0 && minutesValue <= 59) {
      return buildDateForToday(hoursValue, minutesValue);
    }
  }

  return null;
};

export const getMedicationScheduleTimes = (medication: any) => {
  const collected = new Set<string>();
  const explicitValue = normalizeStructuredScheduleTime(medication?.scheduledClockTime);
  if (explicitValue) {
    collected.add(explicitValue);
  }

  const scheduleText = String(medication?.schedule || "");
  const meridiemMatches = Array.from(scheduleText.matchAll(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)\b/gi));
  for (const match of meridiemMatches) {
    const normalized = convertMeridiemMatchToStructuredTime(match);
    if (normalized) {
      collected.add(normalized);
    }
  }

  const twentyFourHourSource = meridiemMatches.length
    ? scheduleText.replace(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)\b/gi, " ")
    : scheduleText;

  for (const match of Array.from(twentyFourHourSource.matchAll(/\b(\d{1,2}):(\d{2})\b/g))) {
    const normalized = normalizeStructuredScheduleTime(`${match[1]}:${match[2]}`);
    if (normalized) {
      collected.add(normalized);
    }
  }

  return Array.from(collected).sort((first, second) => first.localeCompare(second));
};

export const buildMedicationScheduleText = (
  input: any,
  options?: {
    dayPattern?: MedicationScheduleDayPattern;
    scheduleDays?: string[];
    scheduleMonthDays?: number[];
  },
) => {
  const times = Array.isArray(input) ? input : getMedicationScheduleTimes(input);
  const dayPattern = options?.dayPattern || inferMedicationScheduleDayPattern(Array.isArray(input) ? {} : input);
  const scheduleDays = options?.scheduleDays || getMedicationScheduleDays(Array.isArray(input) ? {} : input);
  const scheduleMonthDays = options?.scheduleMonthDays || getMedicationScheduleMonthDays(Array.isArray(input) ? {} : input);
  const timesLabel = times
    .map((time) => formatStructuredScheduleTimeLabel(time))
    .filter(Boolean)
    .join(", ");
  const dayPrefix = buildScheduleDayPrefix(dayPattern, scheduleDays, scheduleMonthDays);

  if (dayPrefix && timesLabel) {
    return `${dayPrefix} at ${timesLabel}`;
  }
  if (dayPrefix) {
    return dayPrefix;
  }
  return timesLabel;
};

export const getMedicationPrimaryScheduleTimeValue = (medication: any) => {
  const scheduleTimes = getMedicationScheduleTimes(medication);
  if (scheduleTimes.length) {
    return scheduleTimes[0];
  }

  const extractedDate = extractScheduleTimeFromText(String(medication?.schedule || ""));
  if (!extractedDate) {
    return "";
  }

  return `${String(extractedDate.getHours()).padStart(2, "0")}:${String(extractedDate.getMinutes()).padStart(2, "0")}`;
};

export const getMedicationScheduleDisplay = (medication: any) => {
  const parts = [medication?.dosage, medication?.route].filter(Boolean);
  const scheduleTimesLabel = buildMedicationScheduleText(medication);
  const scheduleNote = String(medication?.schedule || "").trim();

  if (scheduleTimesLabel) {
    parts.push(scheduleTimesLabel);
  } else if (scheduleNote) {
    parts.push(scheduleNote);
  }

  return parts.join(" • ");
};

export const getMedicationScheduleOccurrences = (medication: any, referenceDate?: Date | string | null): EmarMedicationOccurrence[] => {
  if (medication?.prn) {
    return [{
      occurrenceKey: "prn",
      scheduledTimeKey: "prn",
      scheduledClockTime: "",
      scheduledLabel: "PRN",
      scheduleText: String(medication?.schedule || "").trim(),
    }];
  }

  if (!isMedicationScheduledForDate(medication, referenceDate)) {
    return [];
  }

  const scheduleTimes = getMedicationScheduleTimes(medication);
  if (!scheduleTimes.length) {
    const fallbackClockTime = normalizeStructuredScheduleTime(medication?.scheduledClockTime);
    return [{
      occurrenceKey: fallbackClockTime || "default",
      scheduledTimeKey: fallbackClockTime || "default",
      scheduledClockTime: fallbackClockTime,
      scheduledLabel: String(medication?.schedule || "").trim() || "Unscheduled",
      scheduleText: String(medication?.schedule || "").trim(),
    }];
  }

  return scheduleTimes.map((time) => ({
    occurrenceKey: time,
    scheduledTimeKey: time,
    scheduledClockTime: time,
    scheduledLabel: formatStructuredScheduleTimeLabel(time) || time,
    scheduleText: String(medication?.schedule || "").trim(),
  }));
};

export const parseScheduleTimeForToday = (schedule: string, scheduledClockTime?: string): Date | null => {
  const structuredDate = buildDateForTodayFromStructuredTime(String(scheduledClockTime || ""));
  if (structuredDate) {
    return structuredDate;
  }

  return extractScheduleTimeFromText(schedule);
};

export const getTodayMedicationTimingMeta = (scheduleSource: any, hasEntry: boolean): EmarTimingMeta => {
  if (typeof scheduleSource !== "string" && !isMedicationScheduledForDate(scheduleSource)) {
    return {
      parsed: false,
      scheduledDate: null,
      scheduledLabel: "",
      isDueSoon: false,
      isUpcoming: false,
      isOverdue: false,
    };
  }
  const schedule = typeof scheduleSource === "string"
    ? scheduleSource
    : String(scheduleSource?.schedule || "");
  const scheduledClockTime = typeof scheduleSource === "string"
    ? ""
    : String(scheduleSource?.scheduledClockTime || "");
  const scheduledDate = parseScheduleTimeForToday(schedule, scheduledClockTime);
  if (!scheduledDate) {
    return {
      parsed: false,
      scheduledDate: null,
      scheduledLabel: "",
      isDueSoon: false,
      isUpcoming: false,
      isOverdue: false,
    };
  }

  const now = new Date();
  const scheduledTime = scheduledDate.getTime();
  const nowTime = now.getTime();
  const dueSoonStart = scheduledTime - (30 * 60 * 1000);
  const overdueStart = scheduledTime + (60 * 60 * 1000);

  return {
    parsed: true,
    scheduledDate,
    scheduledLabel: formatTimeLabel(scheduledDate),
    isDueSoon: !hasEntry && nowTime >= dueSoonStart && nowTime <= overdueStart,
    isUpcoming: !hasEntry && nowTime < dueSoonStart,
  isOverdue: !hasEntry && nowTime > overdueStart,
  };
};

export const deriveEmarOccurrenceStatus = (status: any, timingMeta?: EmarTimingMeta | null, isPrn = false) => {
  const normalizedStatus = String(status || "").trim().toLowerCase();
  if (normalizedStatus) {
    return normalizedStatus;
  }
  if (!isPrn && timingMeta?.isOverdue) {
    return "missed";
  }
  return "";
};
